<template>
    <div class="productlist-items">
        <div v-if="topProducts.length" class="row justify-content-center product-list">
            <div v-for="(item, i) in topProducts" :key="item.id" class="col-12 col-lg-6">
                <ObjectsProduct
                    :highlight="true"
                    :show-description="showProductsDescription"
                    :item="item"
                    :list-position="i"
                    @product-redirected-to-url="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Click)"
                    @product-added-to-basket="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Conversion)"
                />
            </div>
        </div>
        <div class="product-list" data-cy="product-list">
            <template v-for="(item, i) in items" :key="item.id">
                <template v-for="(customAddonSaleCarousel, index) in customAddonSaleCarousels" :key="index">
                    <div v-if="i === customAddonSaleCarousel.positionInProductList" class="product-list__carousel">
                        <h2 :class="{ 'mt-4': index === 0 }">
                            {{ customAddonSaleCarousel.title }}
                        </h2>

                        <ElementsCarouselBox
                            :products="customAddonSaleCarousel.products"
                            :banner-image-url="customAddonSaleCarousel.bannerImageUrl"
                            :minimalized-product-visual="!!customAddonSaleCarousel.bannerImageUrl"
                            :url="customAddonSaleCarousel.titleLink"
                            :cta-text="customAddonSaleCarousel.ctaText"
                        />
                    </div>
                </template>
                <div class="product-list__item">
                    <div v-if="item.type === 'catalog_banner'" class="product-list__banner-container">
                        <LayoutCategoryProductListBanner :banner="convertToBanner(item)" />
                        <WarningMessageInBanner
                            v-if="convertToBanner(item).warningMessage?.length"
                            :message="convertToBanner(item).warningMessage"
                            :modal-id="`${item.id}–warningMessage`"
                        />
                    </div>
                    <ObjectsProduct
                        v-else
                        :show-description="showProductsDescription"
                        :show-nutra-rating="showProductsNutraRating || item.nutraRatingIsVisible"
                        :item="item"
                        :lazy-image="i >= 3 && lazyImage"
                        :list-position="topProducts.length + i"
                        :list-title="listTitle"
                        :list-type="listType"
                        :item-list-name="itemListName"
                        @product-redirected-to-url="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Click)"
                        @product-added-to-basket="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Conversion)"
                    />
                </div>
            </template>
        </div>

        <slot />
    </div>
</template>

<script lang="ts">
import { list } from 'postcss'
import WarningMessageInBanner from '~/components/elements/WarningMessageInBanner.vue'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import Product from '~/models/Product/Product'
import { LuigisBoxClickEventTypeEnum } from '~/models/LuigisBox/LuigisBoxClickEventTypeEnum'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import ProductListBanner from '~/models/components/ProductListBanner'

export default {
    components: {
        WarningMessageInBanner,
    },
    props: {
        items: {
            type: Array as PropType<Product[]>,
            default: null,
        },
        topProducts: {
            type: Array as PropType<Product[]>,
            default: null,
        },
        lazyImage: {
            type: Boolean,
            default: true,
        },

        listTitle: {
            type: String,
            default: '',
        },

        listType: {
            type: String,
            default: '',
        },

        showProductsDescription: {
            type: Boolean,
            default: false,
        },

        showProductsNutraRating: {
            type: Boolean,
            default: false,
        },

        customAddonSaleCarousels: {
            type: Array as PropType<
                {
                    title: string
                    titleLink: string
                    ctaText: string
                    products: Product[]
                    bannerImageUrl?: string
                    positionInProductList: number
                }[]
            >,
            default: () => [],
        },
    },

    computed: {
        list() {
            return list
        },

        itemListName() {
            if (this.listType === 'search') {
                return GtmItemListNameEnum.SearchResults
            }

            return GtmItemListNameEnum.Catalogue
        },

        LuigisBoxClickEventTypeEnum() {
            return LuigisBoxClickEventTypeEnum
        },
    },

    methods: {
        convertToBanner(product: Product): ProductListBanner {
            return product as unknown as ProductListBanner
        },

        sendLuigisboxEvent(productUrl: string, type: LuigisBoxClickEventTypeEnum) {
            if (this.listType === 'search') {
                LuigisBoxEventApi.sendClickEvent(type, productUrl)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.productlist-items {
    margin-bottom: 2em;
    container-type: inline-size;
}

$gap-mobile: 8px;
$gap: 36px;
.product-list {
    gap: $gap-mobile;
    padding: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;

    @container (min-width: 800px) {
        grid-template-columns: 1fr 1fr 1fr;
    }

    @container (min-width: 850px) {
        grid-template-columns: 1fr 1fr 1fr 1fr;
    }

    @container (min-width: 1000px) {
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    }

    @include screen-md {
        //padding: 0 8px;
        gap: 36px;
    }

    &__banner-container {
        margin-top: 25px;
        height: 100%;
    }

    &__carousel {
        grid-column: 1 / -1;
    }

    &__item {
        position: relative;
        padding: 0 0 6px;

        @media (max-width: 480px) {
            padding: 0 !important;
        }
    }

    .product {
        height: 100%;

        @media (max-width: 480px) {
            border-bottom: 1px solid $color-grey-dark;
            border-right: 1px solid $color-grey-dark;
            margin: 0;
            border-radius: 0;
        }
        &.product--row {
            @media (max-width: 480px) {
                border-right: 0;
            }
        }
    }

    .product-list__item:nth-child(even) .product {
        @media (max-width: 480px) {
            border-right: 0;
        }
    }
}
</style>
